import "./OCRQueryResultModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { Dropzone } from "components/Documents/shared/DocumentUploadModal";
import { Select } from "components/inputs";
import { useMemo, useRef, useState } from "react";
import { useGetDocumentsQuery } from "services/documents-api";
import { useUploadOCRQueryResultMutation } from "services/rre-profile-ingestion-api";
import { useSelector } from "utils/redux";

import { KaboodleToaster } from "../common";
import KaboodleModal from "./KaboodleModal";

type OCRQueryResultModalProps = {
  isOpen: boolean;
  onClose: () => void;
  fileId?: string | number;
};

type DocumentOption = {
  label: string;
  value: number;
};

const OCRQueryResultModal = ({
  isOpen,
  onClose,
  fileId,
}: OCRQueryResultModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const accessToken = useSelector((state) => state.main.accessToken);
  const [uploadOCRQueryResult, { isLoading }] =
    useUploadOCRQueryResultMutation();
  const fileIdString = fileId?.toString() || "";

  const { data: documentsData, isLoading: isDocumentsLoading } =
    useGetDocumentsQuery(
      {
        fileId: fileIdString,
        sortBy: "-id",
        limit: "1000",
        offset: "0",
      },
      {
        skip: !fileIdString || !isOpen,
        refetchOnMountOrArgChange: true,
      },
    );

  const documents = documentsData?.results || [];

  const filteredDocuments = useMemo(
    () => documents.filter((doc) => doc.extension?.toLowerCase() === ".pdf"),
    [documents],
  );

  const documentOptions: DocumentOption[] = useMemo(
    () =>
      filteredDocuments.map((doc) => ({
        label: doc.original_filename_as_uploaded || doc.original_filename,
        value: doc.id,
      })),
    [filteredDocuments],
  );

  const handleUpload = async () => {
    if (!selectedFile && !selectedDocumentId) {
      KaboodleToaster.error("Please select a file to upload");
      return;
    }

    if (!fileId) {
      KaboodleToaster.error(
        "Could not determine file ID. Please try again from a file-specific page.",
      );
      return;
    }

    try {
      setIsSubmitting(true);
      let fileToUpload: File | null = selectedFile;

      if (!fileToUpload && selectedDocumentId) {
        const selectedDocument = filteredDocuments.find(
          (doc) => doc.id === selectedDocumentId,
        );

        if (!selectedDocument) {
          KaboodleToaster.error(
            "Selected document could not be found. Please try again.",
          );
          return;
        }

        if (selectedDocument.extension?.toLowerCase() !== ".pdf") {
          KaboodleToaster.error("Only PDF files are supported");
          return;
        }

        const headers: HeadersInit = {};
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }

        const response = await fetch(selectedDocument.proxy_url, { headers });
        if (!response.ok) {
          KaboodleToaster.error(
            "Failed to fetch the selected document. Please try again.",
          );
          return;
        }

        const blob = await response.blob();
        const filename =
          selectedDocument.original_filename_as_uploaded ||
          selectedDocument.original_filename ||
          `document-${selectedDocument.id}.pdf`;

        fileToUpload = new File([blob], filename, {
          type: blob.type || "application/pdf",
        });
      } else {
        // validate file type (PDF or PNG only based on the backend API)
        const validFileTypes = ["application/pdf", "image/png"];
        if (!validFileTypes.includes(fileToUpload.type)) {
          KaboodleToaster.error("Only PDF and PNG files are supported");
          return;
        }
      }

      const formData = new FormData();
      formData.append("file", fileToUpload);

      const response = await uploadOCRQueryResult({
        fileId: fileIdString,
        formData,
      }).unwrap();

      KaboodleToaster.success(
        response.message || "File uploaded and submitted for OCR analysis",
      );

      // reset and close
      setSelectedFile(null);
      setSelectedDocumentId(null);
      onClose();
    } catch (error) {
      KaboodleToaster.error(
        error?.data?.error ||
          error?.message ||
          "Failed to upload file. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setSelectedDocumentId(null);
    onClose();
  };

  const isBusy = isLoading || isSubmitting;

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <div className="flex jc-c">
          <Button
            onClick={handleUpload}
            disabled={isBusy || (!selectedFile && !selectedDocumentId)}
          >
            {isBusy ? "Uploading..." : "Submit"}
          </Button>
          <Button onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading="OCR Query Result Upload">
        <div className="OCRQueryResultModal">
          <div className="data-values">
            {isDocumentsLoading ? (
              <div>Loading documents...</div>
            ) : !fileIdString ? (
              <div>File ID is required to load documents.</div>
            ) : filteredDocuments.length === 0 ? (
              <div>No PDF documents found for this file.</div>
            ) : (
              <Select
                name="existing-document"
                label="Existing Document (PDF)"
                options={documentOptions}
                value={
                  documentOptions.find(
                    (option) => option.value === selectedDocumentId,
                  ) || null
                }
                onChange={(option) => {
                  setSelectedDocumentId(
                    option ? (option as DocumentOption).value : null,
                  );
                  if (option) {
                    setSelectedFile(null);
                  }
                }}
                isClearable
                isSearchable={false}
              />
            )}
          </div>
          {!selectedDocumentId && (
            <div className="data-values">
              <label>Upload Coverage Document (PDF or PNG):</label>
              <Dropzone
                selectedFile={selectedFile}
                onFileSelect={(file) => {
                  setSelectedDocumentId(null);
                  setSelectedFile(Array.isArray(file) ? file[0] : file);
                }}
                fileInputRef={fileInputRef}
                maxFiles={1}
              />
            </div>
          )}
          <div className="data-values">
            <p>
              {selectedDocumentId
                ? "Selected document will be submitted to OCR for analysis."
                : "Upload a single PDF or PNG file containing coverage information. This will be submitted to OCR for analysis."}
            </p>
          </div>
        </div>
      </Card>
    </KaboodleModal>
  );
};

export default OCRQueryResultModal;
